/**
 * Temporary API methods not connected to the redux store. Replaces all iron-ajax use in dabih
 *
 *
 */

import type {
  AskRequest,
  AttachmentViewModel,
  CreateUserRequest,
  CreateUserResponse,
  LinkAuthResult,
  LinkAuthUpdateMessage,
  MessageViewModel,
  RevisionViewModel,
  SaveDraftCommand,
  SearchResultHit,
} from '@trinnvis/dabih-api-client';
import {
  AccountsApi,
  AssetsApi,
  ComputersApi,
  CompetenceApi,
  Configuration,
  ConstitutionalDocumentsApi,
  ContractsApi,
  DocumentsApi,
  EmployeesApi,
  EventOccurrencesApi,
  FunctionsApi,
  GuidelinesApi,
  IssuesApi,
  MeetingOccurrencesApi,
  MeetingsApi,
  OrganizationApi,
  PartnersApi,
  ReportsApi,
  RiskAssessmentsApi,
  SubstancesApi,
  TopicsApi,
  TutorialsApi,
} from '@trinnvis/dabih-api-client';
import pDebounce from 'p-debounce';
import { BASE_PATH } from './config.js';
import { Buffer } from 'buffer/index.js';
import { getValidAccessToken } from 'src/store/oauth-browser';
import { createQueue } from 'fetch-queue';
import { robustFetch } from 'src/utilities/robust-fetch.js';
import { runningUnderNativePlatform } from 'src/store/running-under-native-platform';
import {
  logApiClientFetchFailure,
  logApiClientFetchStart,
  logApiClientFetchSuccess,
  logApiClientLoadFailedContext,
} from 'src/store/api-client-logger';

export function decodeToken(token: string): string {
  return Buffer.from(token, 'base64').toString();
}

createQueue('apiClientQueue', {
  concurrent: 1,
});

const apiClientFetchQueue = async (url, fetchOptions) => {
  const method = (fetchOptions?.method ?? 'GET').toUpperCase();

  logApiClientFetchStart(url, method, fetchOptions);

  try {
    // Use robust fetch with retry logic for API calls
    const response = await robustFetch(url, {
      ...fetchOptions,
      retries: 3,
      timeout: 30000,
      silent: false,
    });

    logApiClientFetchSuccess(url, method, response.status, response.statusText, fetchOptions);

    return response;
  } catch (e: any) {
    logApiClientFetchFailure(url, method, e, fetchOptions);
    logApiClientLoadFailedContext(url, e);
    throw e;
  }
};

// Non-queued robust fetch for endpoints used before a token exists
// (signup/signin: createUser, sendPassword, checkEmail). Gives them the same
// retry/timeout/offline handling as authenticated calls without the
// apiClientQueue serialization. (issue #1531)
const apiClientFetch: typeof fetch = (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
  return robustFetch(url, {
    ...init,
    retries: 3,
    timeout: 30000,
    silent: false,
  });
};

export function getBasePath() {
  return BASE_PATH;
}

export class ApiClient {
  get accounts(): AccountsApi {
    return new AccountsApi(this.config);
  }
  get organization(): OrganizationApi {
    return new OrganizationApi(this.config);
  }
  get eventOccurrences(): EventOccurrencesApi {
    return new EventOccurrencesApi(this.config);
  }

  get meetingOccurrences(): MeetingOccurrencesApi {
    return new MeetingOccurrencesApi(this.config);
  }
  get computers(): ComputersApi {
    return new ComputersApi(this.config);
  }

  get functions(): FunctionsApi {
    return new FunctionsApi(this.config);
  }

  get topics(): TopicsApi {
    return new TopicsApi(this.config);
  }

  get documents(): DocumentsApi {
    return new DocumentsApi(this.config);
  }

  get meetings(): MeetingsApi {
    return new MeetingsApi(this.config);
  }

  get guidelines(): GuidelinesApi {
    return new GuidelinesApi(this.config);
  }
  get issues(): IssuesApi {
    return new IssuesApi(this.config);
  }

  get assets(): AssetsApi {
    return new AssetsApi(this.config);
  }
  get reports(): ReportsApi {
    return new ReportsApi(this.config);
  }
  get riskAssessments(): RiskAssessmentsApi {
    return new RiskAssessmentsApi(this.config);
  }

  get partners(): PartnersApi {
    return new PartnersApi(this.config);
  }

  get contracts(): ContractsApi {
    return new ContractsApi(this.config);
  }
  get competence(): CompetenceApi {
    return new CompetenceApi(this.config);
  }
  get constitutionalDocuments(): ConstitutionalDocumentsApi {
    return new ConstitutionalDocumentsApi(this.config);
  }
  get employees(): EmployeesApi {
    return new EmployeesApi(this.config);
  }
  get substances(): SubstancesApi {
    return new SubstancesApi(this.config);
  }

  get tutorials(): TutorialsApi {
    return new TutorialsApi(this.config);
  }

  get config(): Configuration {
    if (runningUnderNativePlatform()) {
      return this.configWithAccessToken;
    }

    const accessToken = localStorage.getItem('access_token');
    const token = localStorage.getItem('dabih-token');
    if (!accessToken && token) {
      // Token auth used from admin
      return this.configWithToken;
    } else {
      return this.configWithAccessToken;
    }
  }

  private get configWithAccessToken(): Configuration {
    return new Configuration({
      basePath: getBasePath(),
      accessToken: () => getValidAccessToken(),
      fetchApi: apiClientFetchQueue,
      headers: {
        'X-Requested-With': __APP_VERSION__,
      },
    });
  }

  private get configWithToken(): Configuration {
    const token = localStorage.getItem('dabih-token');
    let username: string | undefined = undefined;
    let password: string | undefined = undefined;
    if (token) {
      const strings = decodeToken(token).split(':');
      username = strings[0];
      password = strings[1];
    }

    return new Configuration({
      basePath: getBasePath(),
      username: username,
      password: password,
      fetchApi: apiClientFetchQueue,
      headers: {
        'X-Requested-With': __APP_VERSION__,
      },
    });
  }
}

export function createClient(): ApiClient {
  return new ApiClient();
}

// Cannot use apiclient since there is no token available
export async function createUser(requestParameters: CreateUserRequest): Promise<CreateUserResponse> {
  const config = new Configuration({ basePath: getBasePath(), fetchApi: apiClientFetch });
  const api = new AccountsApi(config);

  return api.createUser(requestParameters);
}

export function sendPassword(username: string): Promise<MessageViewModel> {
  const config = new Configuration({ basePath: getBasePath(), fetchApi: apiClientFetch });
  const api = new AccountsApi(config);

  return api.requestPassword({ userEmail: username });
}

export function checkEmail(username: string): Promise<MessageViewModel> {
  const config = new Configuration({ basePath: getBasePath(), fetchApi: apiClientFetch });
  const api = new AccountsApi(config);

  return api.fetchUserStatus({ accountEmail: username });
}

export function linkAuth(token: string, message: LinkAuthUpdateMessage): Promise<LinkAuthResult> {
  const strings = decodeToken(token).split(':');
  const username = strings[0];
  const password = strings[1];
  const config = new Configuration({
    basePath: getBasePath(),
    username: username,
    password: password,
    fetchApi: fetch,
  });
  const api = new AccountsApi(config);

  return api.startLinkAuthentication({
    linkAuthUpdateMessage: message,
  });
}

function assetAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.assets.fetchAttachmentForAsset({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function constitutionalDocumentAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.constitutionalDocuments.fetchAttachmentForConstitutionalDocument({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function contractAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.contracts.fetchAttachmentForContract({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}

function competenceAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.competence.fetchAttachmentForCompetence({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}

function reportSigningAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  signingOrderId: string,
): Promise<AttachmentViewModel> {
  return api.reports.reportSigningAttachment({
    entityId: entityId,
    organizationId: organizationId.toString(),
    signingOrderId: signingOrderId,
  });
}

function contractSigningAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  signingOrderId: string,
): Promise<AttachmentViewModel> {
  return api.contracts.contractSigningAttachment({
    entityId: entityId,
    organizationId: organizationId.toString(),
    signingOrderId: signingOrderId,
  });
}

function constitutionalDocumentSigningAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  signingOrderId: string,
): Promise<AttachmentViewModel> {
  return api.constitutionalDocuments.constitutionalDocumentSigningAttachment({
    entityId: entityId,
    organizationId: organizationId.toString(),
    signingOrderId: signingOrderId,
  });
}
function documentAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.documents.fetchAttachmentForDocument({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function employeeAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.employees.fetchAttachmentForEmployee({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}

function eventOccurrenceAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.eventOccurrences.fetchAttachmentForEventOccurrence({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function meetingOccurrenceAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.meetingOccurrences.fetchAttachmentForMeetingOccurrence({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function functionAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.functions.fetchAttachmentForFunction({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function guidelineAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.guidelines.fetchAttachmentForGuideline({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function issueAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.issues.fetchAttachmentForIssue({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function meetingAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.meetings.fetchAttachmentForMeeting({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function partnerAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.partners.fetchAttachmentForPartner({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function contactAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
  partnerId: string,
): Promise<AttachmentViewModel> {
  return api.partners.fetchAttachmentForPartnerContact({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
    partnerId: partnerId,
  });
}
function reportAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.reports.fetchAttachmentForReport({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function riskAssessmentAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.riskAssessments.fetchAttachmentForRiskAssessment({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function taskAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
  functionId: string,
): Promise<AttachmentViewModel> {
  return api.functions.fetchAttachmentForFunctionTask({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
    functionId: functionId,
  });
}
function externalConnectionAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.computers.fetchAttachmentForExternalConnection({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}
function computerAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.computers.fetchAttachmentForComputer({
    entityId: entityId,
    organizationId: organizationId.toString(),
    fileNameOrAttachmentUuid: attachmentId,
  });
}

function substanceAttachment(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  attachmentId: string,
): Promise<AttachmentViewModel> {
  return api.substances.fetchAttachmentForSubstance({
    entityId: entityId,
    organizationId: organizationId.toString(),
    filenameOrAttachmentUuid: attachmentId,
  });
}

export function attachment(
  api: ApiClient,
  entityType: string,
  entityId: string,
  organizationId: number,
  attachmentId: string,
  parentId?: string,
): Promise<AttachmentViewModel> {
  if (entityId !== 'new') {
    switch (entityType) {
      case 'assets':
        return assetAttachment(api, entityId, organizationId, attachmentId);
      case 'constitutionalDocuments':
        return constitutionalDocumentAttachment(api, entityId, organizationId, attachmentId);
      case 'substances':
        return substanceAttachment(api, entityId, organizationId, attachmentId);
      case 'contracts':
        return contractAttachment(api, entityId, organizationId, attachmentId);
      case 'competence':
        return competenceAttachment(api, entityId, organizationId, attachmentId);
      case 'documents':
        return documentAttachment(api, entityId, organizationId, attachmentId);
      case 'employees':
        return employeeAttachment(api, entityId, organizationId, attachmentId);
      case 'eventOccurrences':
        return eventOccurrenceAttachment(api, entityId, organizationId, attachmentId);
      case 'meetingOccurrences':
        return meetingOccurrenceAttachment(api, entityId, organizationId, attachmentId);
      case 'functions':
        return functionAttachment(api, entityId, organizationId, attachmentId);
      case 'guidelines':
        return guidelineAttachment(api, entityId, organizationId, attachmentId);
      case 'issues':
        return issueAttachment(api, entityId, organizationId, attachmentId);
      case 'meetings':
        return meetingAttachment(api, entityId, organizationId, attachmentId);
      case 'partners':
        return partnerAttachment(api, entityId, organizationId, attachmentId);
      case 'contacts':
        return contactAttachment(api, entityId, organizationId, attachmentId, parentId ?? '');
      case 'reports':
        return reportAttachment(api, entityId, organizationId, attachmentId);
      case 'riskAssessments':
        return riskAssessmentAttachment(api, entityId, organizationId, attachmentId);
      case 'tasks':
        return taskAttachment(api, entityId, organizationId, attachmentId, parentId ?? '');
      case 'externalConnections':
        return externalConnectionAttachment(api, entityId, organizationId, attachmentId);
      case 'computers':
        return computerAttachment(api, entityId, organizationId, attachmentId);
    }
  }

  throw new Error('Illegal state (E541), unexpected attachment entity type ' + entityType);
}

export function signingAttachment(
  api: ApiClient,
  entityType: string,
  entityId: string,
  organizationId: number,
  signingOrderId: string,
): Promise<AttachmentViewModel> {
  if (entityId === 'new') {
    throw new Error('Illegal state (E547), unexpected signing attachment entity type ' + entityType);
  }
  switch (entityType) {
    case 'contracts':
      return contractSigningAttachment(api, entityId, organizationId, signingOrderId);
    case 'constitutionalDocuments':
      return constitutionalDocumentSigningAttachment(api, entityId, organizationId, signingOrderId);
    case 'reports':
      return reportSigningAttachment(api, entityId, organizationId, signingOrderId);
    default:
      throw new Error('Illegal state (E547), unexpected signing attachment entity type ' + entityType);
  }
}

function contractRevisions(api: ApiClient, entityId: string, organizationId: number): Promise<RevisionViewModel[]> {
  return api.contracts.listRevisionsForContract({
    contractId: entityId,
    organizationId: organizationId.toString(),
  });
}

function constitutionalDocumentRevisions(
  api: ApiClient,
  entityId: string,
  organizationId: number,
): Promise<RevisionViewModel[]> {
  return api.constitutionalDocuments.listRevisionsForConstitutionalDocument({
    constitutionalDocumentId: entityId,
    organizationId: organizationId.toString(),
  });
}

function documentRevisions(api: ApiClient, entityId: string, organizationId: number): Promise<RevisionViewModel[]> {
  return api.documents.listRevisionsForDocument({
    documentId: entityId,
    organizationId: organizationId.toString(),
  });
}

function functionRevisions(api: ApiClient, entityId: string, organizationId: number): Promise<RevisionViewModel[]> {
  return api.functions.listRevisionsForFunction({
    functionId: entityId,
    organizationId: organizationId.toString(),
  });
}

function guidelineRevisions(api: ApiClient, entityId: string, organizationId: number): Promise<RevisionViewModel[]> {
  return api.guidelines.listRevisionsForGuideline({
    guidelineId: entityId,
    organizationId: organizationId.toString(),
  });
}

function reportRevisions(api: ApiClient, entityId: string, organizationId: number): Promise<RevisionViewModel[]> {
  return api.reports.listRevisionsForReport({
    reportId: entityId,
    organizationId: organizationId.toString(),
  });
}

function riskAssessmentRevisions(
  api: ApiClient,
  entityId: string,
  organizationId: number,
): Promise<RevisionViewModel[]> {
  return api.riskAssessments.listRevisionsForRiskAssessment({
    riskAssessmentId: entityId,
    organizationId: organizationId.toString(),
  });
}

function taskRevisions(
  api: ApiClient,
  entityId: string,
  organizationId: number,
  parentEntityId: string,
): Promise<RevisionViewModel[]> {
  return api.functions.listRevisionsForFunctionTask({
    functionId: parentEntityId,
    taskId: entityId,
    organizationId: organizationId.toString(),
  });
}

const supportedEntityTypesForDraft = [
  'assets',
  'constitutionalDocuments',
  'contracts',
  'employees',
  'documents',
  'functions',
  'guidelines',
  'issues',
  'meetings',
  'partners',
  'contacts',
  'reports',
  'riskAssessments',
  'substances',
  'tasks',
  'eventOccurrences',
  'meetingOccurrences',
];

export function draft(
  api: ApiClient,

  entityType: string,
  entityId: string,
  organizationId: number,
): Promise<SaveDraftCommand> {
  if (entityId !== 'new' && supportedEntityTypesForDraft.includes(entityType)) {
    return api.organization.fetchDraft({
      entityType: entityType,
      entityId: entityId,
      organizationId: organizationId.toString(),
    });
  }

  return Promise.reject(new Error('Entity type ' + entityType + ' not supported'));
}

export function revisions(
  api: ApiClient,
  entityType: string,
  entityId: string,
  organizationId: number,
  parentEntityId?: string,
): Promise<RevisionViewModel[]> {
  if (entityId !== 'new') {
    switch (entityType) {
      case 'constitutionalDocuments':
        return constitutionalDocumentRevisions(api, entityId, organizationId);
      case 'contracts':
        return contractRevisions(api, entityId, organizationId);
      case 'documents':
        return documentRevisions(api, entityId, organizationId);
      case 'functions':
        return functionRevisions(api, entityId, organizationId);
      case 'guidelines':
        return guidelineRevisions(api, entityId, organizationId);
      case 'reports':
        return reportRevisions(api, entityId, organizationId);
      case 'riskAssessments':
        return riskAssessmentRevisions(api, entityId, organizationId);
      case 'tasks':
        if (parentEntityId === undefined) {
          throw new Error('Illegal state (E601), arguments for task revisions, parent missing');
        }
        return taskRevisions(api, entityId, organizationId, parentEntityId);
    }
  }

  return Promise.resolve([]);
}

const debounceSearch = pDebounce((api: ApiClient, query: string, organizationId: string) => {
  return search(api, query, organizationId);
}, 300);
export function debouncedSearch(api: ApiClient, query?: string, organizationId?: string): Promise<SearchResultHit[]> {
  if (query === undefined || organizationId === undefined) {
    return Promise.resolve([]);
  }
  if (query.length < 3) {
    return Promise.resolve([]);
  }
  return debounceSearch(api, query, organizationId);
}

export async function search(api: ApiClient, query: string, organizationId: string): Promise<SearchResultHit[]> {
  return api.organization.searchOrganization({
    query: query,
    limit: 200,
    organizationId: organizationId,
  });
}

export function saveDraft(
  token: string,
  organizationId: string,
  entityType: string,
  entityId: string,
  properties: Record<string, unknown>,
) {
  fetch(BASE_PATH + '/organizations/' + organizationId + '/drafts/' + entityType + '/' + entityId, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + token,
    },
    method: 'PUT',
    body: JSON.stringify(properties),
  })
    .then(function () {
      console.log('Saved draft', organizationId, entityType, entityId);
    })
    .catch(function (res) {
      console.log(res);
    });
}

export async function ask(organizationId: string, prompt: string): Promise<string> {
  const api = createClient();
  const request: AskRequest = {
    askCommand: {
      model: 'chat-gpt',
      messages: [{ text: prompt }],
    },
    organizationId: organizationId,
  };
  const r = await api.organization.ask(request);
  return r.text ?? '';
}

const asked: Map<string, string> = new Map<string, string>();

export async function memoizedAsk(organizationId: string, prompt: string): Promise<string> {
  const key = organizationId + '//' + prompt;
  if (asked) {
    if (asked.has(key)) {
      return asked.get(key) ?? '';
    } else {
      const r = await ask(organizationId, prompt);
      asked.set(key, r);
      return r;
    }
  }
  return '';
}

export async function postTripletex(
  e: string,
  ym: string,
): Promise<{ date: string; hours: number; projectDisplayName: string; activityDisplayName: string }[]> {
  const token = await getValidAccessToken();
  const response = await fetch('https://5hi2slvqqcjguwx5rovldkmqaa0zvnav.lambda-url.eu-central-1.on.aws/', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    method: 'POST',
    body: JSON.stringify({ employeeUuid: e, yearMonth: ym }),
  });

  return await response.json();
}
